// ✅ src/pages/VehicleV2/VehicleDailyProgressListing.jsx
// Slim table + view modal to avoid overflow.
// View modal shows full row; Open SLT still opens VehicleDailyProgressEntrySLT.

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaFilter,
  FaSearch,
  FaTrash,
  FaEye,
  FaUndo,
  FaTimes,
  FaArrowLeft,
} from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/dailyProgressListing";

const toText = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v);
};

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const clean = (v) => String(v ?? "").trim();

const VehicleDailyProgressListing = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid") || "8";
  const branchType = getLS("branchType") || getLS("branchtype") || "HO";
  const userId = getLS("userId") || getLS("loginuserid") || getLS("loginUserId");
  const loginUserId =
    (typeof window !== "undefined"
      ? sessionStorage.getItem("loginUserId")
      : "") ||
    userId ||
    "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const queryVehicleType = useMemo(() => {
    const sp = new URLSearchParams(location?.search || "");
    return clean(sp.get("vehicleType") || sp.get("type") || "O").toUpperCase();
  }, [location]);

  const [vehicleType, setVehicleType] = useState(
    queryVehicleType === "H" ? "H" : "O"
  );

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  const [stores, setStores] = useState([]);
  const [vehicleBasisList, setVehicleBasisList] = useState([]);
  const [vehicleSubTypes, setVehicleSubTypes] = useState([]);
  const [hiredFromList, setHiredFromList] = useState([]);

  const [filters, setFilters] = useState({
    storeId: "Select",
    hiredFrom: "Select",
    vehicleBasisId: "Select",
    vehicleSubTypeId: "Select",
    vehicleCode: "",
    vehicleName: "",
  });

  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const isBusy = loading || pageLoading || deleting;

  const totalPages = useMemo(() => {
    const t = Math.ceil(total / limit);
    return t > 0 ? t : 1;
  }, [total, limit]);

  const selectAll = useMemo(() => {
    return rows.length > 0 && selectedIds.length === rows.length;
  }, [rows, selectedIds]);

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const cellClass = `px-4 py-4 text-sm ${
    isDark ? "text-gray-300" : "text-gray-900"
  }`;

  const pagerBtn = (enabled) =>
    `px-4 py-2 rounded-lg border text-sm font-semibold ${
      enabled
        ? isDark
          ? "border-gray-600 text-gray-200 hover:bg-gray-700"
          : "border-gray-300 text-gray-700 hover:bg-white"
        : "opacity-50 cursor-not-allowed border-gray-300"
    }`;

  const mapDropdownItem = useCallback((item) => {
    const raw = item || {};
    return {
      id: toText(
        raw?.id ??
          raw?.value ??
          raw?.fld_storeid ??
          raw?.fld_vehicle_basisid ??
          raw?.fld_vehicletypeindex ??
          raw?.fld_hiredfromid ??
          raw?.fld_id
      ),
      name: toText(
        raw?.name ??
          raw?.text ??
          raw?.label ??
          raw?.fld_storename ??
          raw?.fld_vehicle_basistype ??
          raw?.fld_vehiclebasis ??
          raw?.fld_vehicletypename ??
          raw?.fld_hiredfrom ??
          raw?.fld_name
      ),
      _raw: raw,
    };
  }, []);

  const mapRow = useCallback((r) => {
    const raw = r || {};
    return {
      id: toInt(
        raw?.id ??
          raw?.vehicleId ??
          raw?.fld_vehicleid ??
          raw?.mainId ??
          raw?.fld_id ??
          0
      ),
      mainType: toText(
        raw?.mainType ??
          raw?.type ??
          raw?.vehicleMainType ??
          raw?.fld_vehiclemaintype ??
          raw?.hiredFrom
      ),
      code: toText(raw?.code ?? raw?.vehicleCode ?? raw?.fld_vehicle_code),
      regNo: toText(raw?.regNo ?? raw?.vehicleRegNo ?? raw?.fld_vehicle_regno),
      name: toText(raw?.name ?? raw?.vehicleName ?? raw?.fld_vehicle_name),
      basis: toText(
        raw?.basis ?? raw?.vehicleBasis ?? raw?.fld_vehicle_basistype
      ),
      hiredFrom: toText(
        raw?.hiredFrom ?? raw?.supplierName ?? raw?.fld_hiredfrom
      ),
      subType: toText(raw?.subType ?? raw?.fld_vehicle_subtypename),
      branchName: toText(raw?.branchName ?? raw?.fld_branchname),
      _raw: raw,
    };
  }, []);

  const appliedFilters = useMemo(
    () => ({
      branchId: toInt(branchId, 0),
      loginUserId: toInt(loginUserId, 0),
      branchType: clean(branchType),
      vehicleType: clean(vehicleType || "O"),
      storeId: clean(filters.storeId || "Select"),
      vehicleBasisId: clean(filters.vehicleBasisId || "Select"),
      vehicleSubTypeId: clean(filters.vehicleSubTypeId || "Select"),
      vehicleCode: clean(filters.vehicleCode),
      vehicleName: clean(filters.vehicleName),
      hiredFrom:
        clean(vehicleType) === "H"
          ? clean(filters.hiredFrom || "Select")
          : "Select",
    }),
    [branchId, loginUserId, branchType, vehicleType, filters]
  );

  const paginationText = useMemo(() => {
    const from = total === 0 ? 0 : (page - 1) * limit + 1;
    const to = Math.min(total, page * limit);
    return `${from}-${to} of ${total}`;
  }, [page, limit, total]);

  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);
      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          branchId: toInt(branchId, 0),
          loginUserId: toInt(loginUserId, 0),
          branchType: clean(branchType),
        },
        headers: commonHeaders,
      });

      if (!data?.success) throw new Error(data?.message || "Init failed");

      const dd = data?.dropdowns || data || {};
      setStores(Array.isArray(dd?.stores) ? dd.stores.map(mapDropdownItem) : []);
      setVehicleBasisList(
        Array.isArray(dd?.vehicleBasis) ? dd.vehicleBasis.map(mapDropdownItem) : []
      );
      setVehicleSubTypes(
        Array.isArray(dd?.vehicleSubTypes)
          ? dd.vehicleSubTypes.map(mapDropdownItem)
          : []
      );
      setHiredFromList(
        Array.isArray(dd?.hiredFrom) ? dd.hiredFrom.map(mapDropdownItem) : []
      );

      const defaultStoreId = toText(dd?.defaultStoreId, "Select") || "Select";
      setFilters((prev) => ({ ...prev, storeId: defaultStoreId }));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Init failed");
    } finally {
      setPageLoading(false);
    }
  }, [branchId, loginUserId, branchType, commonHeaders, mapDropdownItem]);

  const loadList = useCallback(
    async (nextPage = page, nextLimit = limit, overrideParams = {}) => {
      try {
        setLoading(true);
        const params = {
          ...appliedFilters,
          page: toInt(nextPage, 1),
          limit: toInt(nextLimit, 10),
          ...overrideParams,
        };

        const { data } = await axios.get(`${API_BASE}/list`, {
          params,
          headers: commonHeaders,
        });

        if (!data?.success) throw new Error(data?.message || "Failed to load list");

        const list = Array.isArray(data?.list) ? data.list.map(mapRow) : [];
        setRows(list);
        setSelectedIds([]);
        setTotal(toInt(data?.total ?? list.length, 0));
        setPage(toInt(data?.page, nextPage));
        setLimit(toInt(data?.limit, nextLimit));
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.message || err?.message || "List failed");
        setRows([]);
        setSelectedIds([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [page, limit, appliedFilters, commonHeaders, mapRow]
  );

  const handleSelectAll = () => {
    if (!rows.length) return;
    if (selectAll) setSelectedIds([]);
    else setSelectedIds(rows.map((r) => r.id));
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    setPage(1);
    loadList(1, limit);
  };

  const handleReset = () => {
    const defaultStoreId =
      stores.find((x) => clean(x.id) !== "Select")?.id ||
      filters.storeId ||
      "Select";
    setFilters({
      storeId: toText(defaultStoreId) || "Select",
      hiredFrom: "Select",
      vehicleBasisId: "Select",
      vehicleSubTypeId: "Select",
      vehicleCode: "",
      vehicleName: "",
    });
    setSelectedIds([]);
  };

  const handleViewAll = () => {
    const newVehicleType = "O";
    setVehicleType(newVehicleType);
    setPage(1);
    const defaultStoreId = toText(
      stores.find((x) => clean(x.id) !== "Select")?.id ||
        filters.storeId ||
        "Select"
    );
    const cleared = {
      storeId: defaultStoreId || "Select",
      hiredFrom: "Select",
      vehicleBasisId: "Select",
      vehicleSubTypeId: "Select",
      vehicleCode: "",
      vehicleName: "",
    };
    setFilters(cleared);
    loadList(1, limit, {
      vehicleType: newVehicleType,
      ...cleared,
    });
  };

  const openSlt = (row) => {
    const vid = toInt(row?.id, 0);
    if (!vid) {
      toast.warn("Vehicle Id not found");
      return;
    }
    try {
      localStorage.setItem("vehicleId", String(vid));
      localStorage.setItem("VehicleID", String(vid));
    } catch {}
    window.open(
      `/vehicle/VehicleDailyProgressEntrySLT?vehicleId=${vid}`,
      "_blank"
    );
  };

  const handleDelete = async () => {
    if (selectedIds.length === 0) {
      toast.warn("Please select at least one record");
      return;
    }
    try {
      setDeleting(true);
      const { data } = await axios.post(
        `${API_BASE}/delete`,
        {
          ids: selectedIds,
          loginUserId: toInt(loginUserId, 0),
          branchId: toInt(branchId, 0),
          branchType: clean(branchType),
        },
        { headers: commonHeaders }
      );
      if (!data?.success) throw new Error(data?.message || "Delete failed");
      toast.success(data?.message || `${selectedIds.length} record(s) deleted`);
      setSelectedIds([]);
      await loadList(page, limit);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Delete API not available / Delete failed"
      );
    } finally {
      setDeleting(false);
    }
  };

  const canPrev = page > 1;
  const canNext = page < totalPages;

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    loadList(page, limit);
  }, [page, limit, vehicleType, loadList]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              VEHICLE DAILY PROGRESS ENTRY
            </span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate(-1)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                <FaArrowLeft />
                Back
              </button>

              <h2
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Vehicle Daily Progress Entry Listing
              </h2>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark
                    ? "bg-blue-900 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>
            </div>

            <div className="flex items-center gap-3">
              {isBusy && (
                <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-500"}`}>
                  {deleting ? "Deleting..." : "Loading..."}
                </span>
              )}
              <button
                onClick={() => setShowFilters((p) => !p)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border ${
                  isDark
                    ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-white"
                }`}
              >
                <FaFilter />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`p-6 rounded-lg mb-6 ${
                isDark ? "bg-gray-800/50" : "bg-blue-50"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 ${
                  isDark ? "text-white" : "text-blue-900"
                }`}
              >
                Filtration Area
              </h3>

              <div className="mb-6 flex items-center gap-6 flex-wrap">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="vehicleType"
                    value="O"
                    checked={vehicleType === "O"}
                    onChange={() => {
                      setVehicleType("O");
                      setPage(1);
                      setFilters((prev) => ({ ...prev, hiredFrom: "Select" }));
                    }}
                    disabled={isBusy}
                  />
                  <span className={isDark ? "text-gray-200" : "text-gray-800"}>
                    Own Vehicle
                  </span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="vehicleType"
                    value="H"
                    checked={vehicleType === "H"}
                    onChange={() => {
                      setVehicleType("H");
                      setPage(1);
                    }}
                    disabled={isBusy}
                  />
                  <span className={isDark ? "text-gray-200" : "text-gray-800"}>
                    Hire Vehicle
                  </span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    value={filters.storeId}
                    onChange={(e) => handleFilterChange("storeId", e.target.value)}
                    className={inputClass}
                    disabled
                  >
                    <option value="Select">Select</option>
                    {stores.map((s, idx) => (
                      <option key={`${s.id}-${idx}`} value={toText(s.id)}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Supplier Hired From</label>
                  <select
                    value={filters.hiredFrom}
                    onChange={(e) => handleFilterChange("hiredFrom", e.target.value)}
                    className={inputClass}
                    disabled={vehicleType !== "H" || isBusy}
                  >
                    <option value="Select">Select</option>
                    {hiredFromList.map((h, idx) => (
                      <option key={`${h.id}-${idx}`} value={toText(h.id)}>
                        {h.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Basis</label>
                  <select
                    value={filters.vehicleBasisId}
                    onChange={(e) =>
                      handleFilterChange("vehicleBasisId", e.target.value)
                    }
                    className={inputClass}
                    disabled={isBusy}
                  >
                    <option value="Select">Select</option>
                    {vehicleBasisList.map((b, idx) => (
                      <option key={`${b.id}-${idx}`} value={toText(b.id)}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Sub Type</label>
                  <select
                    value={filters.vehicleSubTypeId}
                    onChange={(e) =>
                      handleFilterChange("vehicleSubTypeId", e.target.value)
                    }
                    className={inputClass}
                    disabled={isBusy}
                  >
                    <option value="Select">Select</option>
                    {vehicleSubTypes.map((t, idx) => (
                      <option key={`${t.id}-${idx}`} value={toText(t.id)}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    value={filters.vehicleCode}
                    onChange={(e) =>
                      handleFilterChange("vehicleCode", e.target.value)
                    }
                    className={inputClass}
                    placeholder="Type vehicle code"
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    value={filters.vehicleName}
                    onChange={(e) =>
                      handleFilterChange("vehicleName", e.target.value)
                    }
                    className={inputClass}
                    placeholder="Type vehicle name"
                    disabled={isBusy}
                  />
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleReset}
                  disabled={isBusy}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                    isDark
                      ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-white"
                  }`}
                >
                  <FaUndo />
                  Clear All Filters
                </button>

                <div className="flex gap-3">
                  <button
                    onClick={handleSearch}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 text-white ${buttonClass} ${hoverClass}`}
                  >
                    <FaSearch />
                    Search
                  </button>
                  <button
                    onClick={handleViewAll}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    View All
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-hidden rounded-lg border shadow-lg">
            {isBusy && rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  No records found
                </div>
              </div>
            ) : (
              <>
                <table className={`w-full table-fixed ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-left text-sm font-semibold w-16">S.No</th>
                      <th className="px-4 py-4 text-left text-sm font-semibold">Vehicle Code</th>
                      <th className="px-4 py-4 text-left text-sm font-semibold">Vehicle Name</th>
                      <th className="px-4 py-4 text-left text-sm font-semibold">Reg No</th>
                      <th className="px-4 py-4 text-center text-sm font-semibold w-20">View</th>
                      <th className="px-4 py-4 text-center text-sm font-semibold w-16">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          disabled={isBusy || rows.length === 0}
                          className="w-4 h-4 cursor-pointer"
                          title="Select All"
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                    {rows.map((r, idx) => (
                      <tr
                        key={r.id || idx}
                        className={`${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-white"
                            : isDark
                            ? "bg-gray-750"
                            : "bg-gray-50"
                        } transition-colors ${
                          isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                        }`}
                      >
                        <td className={cellClass}>{(page - 1) * limit + idx + 1}</td>
                        <td className={`px-4 py-4 text-sm font-semibold truncate ${isDark ? "text-green-400" : "text-green-600"}`}>
                          {r.code || "-"}
                        </td>
                        <td className={`px-4 py-4 text-sm font-medium truncate ${isDark ? "text-white" : "text-gray-900"}`}>
                          {r.name || "-"}
                        </td>
                        <td className={`${cellClass} truncate`}>{r.regNo || "-"}</td>
                        <td className="px-4 py-4 text-center">
                          <button
                            onClick={() => setViewRow(r)}
                            disabled={isBusy}
                            className="text-blue-500 hover:text-blue-600 text-lg"
                            title="View"
                          >
                            <FaEye />
                          </button>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(r.id)}
                            onChange={() => toggleSelect(r.id)}
                            disabled={isBusy}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    {paginationText}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      disabled={!canPrev || isBusy}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className={pagerBtn(canPrev && !isBusy)}
                    >
                      Prev
                    </button>
                    <div className={`text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                      Page {page} / {totalPages}
                    </div>
                    <button
                      disabled={!canNext || isBusy}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      className={pagerBtn(canNext && !isBusy)}
                    >
                      Next
                    </button>
                    <select
                      value={limit}
                      onChange={(e) => {
                        setLimit(Number(e.target.value));
                        setPage(1);
                      }}
                      disabled={isBusy}
                      className={inputClass}
                      style={{ width: 120 }}
                    >
                      {[10, 20, 50, 100].map((n) => (
                        <option key={n} value={n}>
                          {n} / page
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end mt-6">
            <button
              onClick={handleDelete}
              disabled={selectedIds.length === 0 || isBusy}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${
                selectedIds.length === 0 || isBusy
                  ? "bg-gray-400 cursor-not-allowed"
                  : `${buttonClass} ${hoverClass}`
              }`}
            >
              <FaTrash />
              {deleting ? "Deleting..." : "Delete Selected"}
            </button>
          </div>

          {viewRow && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
              onClick={() => setViewRow(null)}
            >
              <div
                className={`w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl shadow-2xl border ${
                  isDark
                    ? "bg-gray-900 border-gray-700 text-white"
                    : "bg-white border-gray-200 text-gray-900"
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                  <div>
                    <h3 className="text-xl font-bold">Vehicle Details</h3>
                    <p className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      {viewRow.code || viewRow.name || "-"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setViewRow(null)}
                    className={`p-2 rounded-lg ${isDark ? "hover:bg-gray-800 text-gray-300" : "hover:bg-gray-100 text-gray-600"}`}
                  >
                    <FaTimes />
                  </button>
                </div>

                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    ["Vehicle Main Type", viewRow.mainType],
                    ["Vehicle Code", viewRow.code],
                    ["Vehicle Name", viewRow.name],
                    ["Vehicle Reg No", viewRow.regNo],
                    ["Vehicle Basis", viewRow.basis],
                    ["Hired From", viewRow.hiredFrom],
                    ["Vehicle Sub Type", viewRow.subType],
                    ["Branch", viewRow.branchName],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className={`p-3 rounded-lg border ${
                        isDark ? "bg-gray-800/60 border-gray-700" : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        {label}
                      </div>
                      <div className="text-sm font-medium break-words">{value || "-"}</div>
                    </div>
                  ))}
                </div>

                <div className={`flex justify-end gap-2 px-6 py-4 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                  <button
                    type="button"
                    onClick={() => setViewRow(null)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-800"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={() => openSlt(viewRow)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${buttonClass} ${hoverClass}`}
                  >
                    Open SLT
                  </button>
                </div>
              </div>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleDailyProgressListing;